export type TarotCard = {
  id: number; // 0..77
  arcana: 'major' | 'minor';
  suit?: 'Wands' | 'Cups' | 'Swords' | 'Pentacles';
  rank?: number; // 1..14 for minor (1=Ace, 11=Page, 12=Knight, 13=Queen, 14=King)
  code: string; // e.g., 00-TheFool, Cups01
  filename: string; // exact filename in uploads/tarot-assets/cards
  zhName: string; // 中文名（不含正逆位）
};

// 大阿尔克那中文名表（0..21）
const MAJOR_ZH = [
  '愚者',
  '魔术师',
  '女祭司',
  '皇后',
  '皇帝',
  '教皇',
  '恋人',
  '战车',
  '力量',
  '隐者',
  '命运之轮',
  '正义',
  '倒吊人',
  '死神',
  '节制',
  '恶魔',
  '高塔',
  '星星',
  '月亮',
  '太阳',
  '审判',
  '世界'
];

// 花色中文
const SUIT_ZH: Record<'Wands' | 'Cups' | 'Swords' | 'Pentacles', string> = {
  Wands: '权杖',
  Cups: '圣杯',
  Swords: '宝剑',
  Pentacles: '星币'
};

// 数字中文（用于 2..10），1=王牌
const NUM_ZH: Record<number, string> = {
  1: '王牌',
  2: '二',
  3: '三',
  4: '四',
  5: '五',
  6: '六',
  7: '七',
  8: '八',
  9: '九',
  10: '十',
  11: '侍者',
  12: '骑士',
  13: '女王',
  14: '国王'
};

// 生成小阿尔克那名称
function minorZhName(suit: TarotCard['suit'], rank: number): string {
  if (!suit) return '';
  const prefix = SUIT_ZH[suit];
  return `${prefix}${NUM_ZH[rank]}`;
}

// 构造 78 张牌映射
const cards: TarotCard[] = [];

// 0..21 大阿尔克那，对应文件 00-TheFool.png .. 21-TheWorld.png
const MAJOR_CODES = [
  '00-TheFool', '01-TheMagician', '02-TheHighPriestess', '03-TheEmpress', '04-TheEmperor',
  '05-TheHierophant', '06-TheLovers', '07-TheChariot', '08-Strength', '09-TheHermit',
  '10-WheelOfFortune', '11-Justice', '12-TheHangedMan', '13-Death', '14-Temperance',
  '15-TheDevil', '16-TheTower', '17-TheStar', '18-TheMoon', '19-TheSun', '20-Judgement', '21-TheWorld'
];

for (let i = 0; i < 22; i++) {
  const code = MAJOR_CODES[i];
  cards.push({
    id: i,
    arcana: 'major',
    code,
    filename: `${code}.png`,
    zhName: MAJOR_ZH[i]
  });
}

// 小阿尔克那按文件夹实际顺序：Cups(22..35), Pentacles(36..49), Swords(50..63), Wands(64..77)
const SUITS: TarotCard['suit'][] = ['Cups', 'Pentacles', 'Swords', 'Wands'];
let id = 22;
for (const suit of SUITS) {
  for (let rank = 1; rank <= 14; rank++) {
    const code = `${suit}${String(rank).padStart(2, '0')}`; // e.g., Cups01
    cards.push({
      id,
      arcana: 'minor',
      suit,
      rank,
      code,
      filename: `${code}.png`,
      zhName: minorZhName(suit, rank)
    });
    id++;
  }
}

export const TAROT_CARDS: ReadonlyArray<TarotCard> = cards;

export function getCardById(cardId: number): TarotCard | undefined {
  return TAROT_CARDS[cardId];
}

export function getCardFilenameById(cardId: number): string | undefined {
  return TAROT_CARDS[cardId]?.filename;
}

export function getCardZhNameById(cardId: number): string | undefined {
  return TAROT_CARDS[cardId]?.zhName;
}

